import type { AxiosResponse } from 'axios'
import type { ResponseWrapper } from '@/api/types/base.types'
import { isError } from '@/helpers/checkQuery'
import type { CrmIntegrationSendResponse } from '../types/crm-integration-chat.types'

/** HTTP 200 + message_id: бэк принял сообщение (доставка у провайдера может быть pending). */
const SEND_SUCCESS_STATUSES = new Set(['sent', 'queued', 'pending'])

const normalizeSendSuccessStatus = (
  rawStatus: string | null,
): CrmIntegrationSendResponse['status'] => {
  if (rawStatus === 'queued') return 'queued'
  if (rawStatus === 'pending') return 'pending'
  return 'sent'
}

export const parseCrmIntegrationSendResponse = (
  data: unknown,
): CrmIntegrationSendResponse | null => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return null
  }

  const record = data as Record<string, unknown>
  const rawStatus = typeof record.status === 'string' ? record.status : null

  if (rawStatus != null && !SEND_SUCCESS_STATUSES.has(rawStatus)) {
    return null
  }

  const messageId = Number(record.message_id ?? record.id)
  if (!Number.isFinite(messageId) || messageId <= 0) {
    return null
  }

  const providerResult =
    record.provider_result != null &&
    typeof record.provider_result === 'object' &&
    !Array.isArray(record.provider_result)
      ? (record.provider_result as Record<string, unknown>)
      : {}

  return {
    status: normalizeSendSuccessStatus(rawStatus),
    message_id: messageId,
    provider_result: providerResult,
  }
}

const collectSendResponsePayloadCandidates = (res: unknown): unknown[] => {
  if (!res || typeof res !== 'object') {
    return []
  }

  const candidates: unknown[] = []
  const pushWrapper = (wrapper: unknown) => {
    if (!wrapper || typeof wrapper !== 'object' || Array.isArray(wrapper)) {
      return
    }

    const record = wrapper as ResponseWrapper<unknown>
    if (record.data != null) {
      candidates.push(record.data)
    }

    candidates.push(wrapper)
  }

  const axiosRes = res as AxiosResponse<ResponseWrapper<unknown>>
  if (axiosRes.data != null) {
    pushWrapper(axiosRes.data)
  }

  const errorResponse = (res as { response?: AxiosResponse<ResponseWrapper<unknown>> })
    .response
  if (errorResponse?.data != null) {
    pushWrapper(errorResponse.data)
  }

  return candidates
}

/** Разбирает ответ send даже при «ломаных» HTTP/axios-обёртках (500 после фактической доставки). */
export const resolveCrmIntegrationSendResponse = (
  res: unknown,
): CrmIntegrationSendResponse | null => {
  for (const candidate of collectSendResponsePayloadCandidates(res)) {
    const parsed = parseCrmIntegrationSendResponse(candidate)
    if (parsed != null) {
      return parsed
    }
  }

  return null
}

export const isCrmIntegrationSendAxiosError = (
  res: unknown,
): res is AxiosResponse<ResponseWrapper<unknown>> => {
  if (!res || typeof res !== 'object') {
    return false
  }

  return isError(res as AxiosResponse<ResponseWrapper<unknown>>)
}
